import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { fetchTakeawayOrders } from "../../../../../services/pos/Tables";
import CreateOrderForm from "../../MakeOrderResponsive/MobileMakeOrder/takeawayMobile/OrderDetails/Openorder/CreateOrderForm";
import TakeawayModalContent from "../../MakeOrderResponsive/MobileMakeOrder/takeawayMobile/TakeawayModalContent";
import { useSettings } from "../../../../../context/SettingsContext";
import OrderItemsDialog from "../DeliverySectionTap/OrderItemsDialog";

const TakeawaySection = () => {
  const { mode } = useContext(ThemeContext);
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState("الكل");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isAddItemsOpen, setIsAddItemsOpen] = useState(false);
  const { settings, loading, error } = useSettings();

  // NEW: حالة Dialog عرض أصناف الأوردر
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [selectedOrderIdForItems, setSelectedOrderIdForItems] = useState(null);

  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const parts = timeStr.split(":");
    const h = Number(parts[0] ?? 0);
    const m = parts[1] ?? "00";
    const ampm = h >= 12 ? "م" : "ص";
    const hour12 = h % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const loadOrders = async () => {
    try {
      const branchId = localStorage.getItem("branch_id");
      if (!branchId) throw new Error("❌ لا يوجد branch_id في localStorage");

      const data = await fetchTakeawayOrders(branchId);

      let orderIncrementKey = "general_increment";
      if (settings?.invoice_incremant === "daily_increment") orderIncrementKey = "daily_increment";
      else if (settings?.invoice_incremant === "monthly_increment") orderIncrementKey = "monthly_increment";

      const enriched = data.map((item) => ({
        id: item.id,
        status: item.status === "pending" ? "في التجهيز" : "مكتمل",
        customer: item.customer?.name || "-",
        pilotName: item.delivery?.name || "-",
        phone: item.customer?.phone1 || "",
        address: item.customer?.address1 || "",
        time: formatTime12(item.open_time),
        total: item.total || 0,
        orderNumber: `#${item[orderIncrementKey] ?? item.general_increment ?? "-"}`,
        timer: 0,
        // NEW: عدد الأصناف في الطلب
        itemsCount: Array.isArray(item.items) ? item.items.length : 0,
        items: (item.items || []).map((it) => ({
          id: it.id,
          product_name: it.product_name,
          quantity: it.quantity,
          price: it.price,
          total: it.total,
        })),
      }));

      setOrders(enriched);
    } catch (err) {
      console.error("❌ فشل تحميل طلبات التيك أواي:", err.message);
    }
  };

  useEffect(() => {
    if (settings && !loading) loadOrders();
  }, [settings, loading]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadOrders();
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem("current_component", "takeaway");
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F7" || e.keyCode === 118) {
        e.preventDefault();
        setIsCreateOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const filteredOrders = orders.filter((order) => {
    const searchFields = [
      order.customer,
      order.pilotName,
      order.phone,
      order.address,
      order.time,
      order.total.toString(),
      order.orderNumber,
    ].join(" ").toLowerCase();

    return (filterStatus === "الكل" || order.status === filterStatus) && searchFields.includes(searchQuery.toLowerCase());
  });

  // NEW: دالة فتح Dialog عرض الأصناف
  const handleOpenOrderItems = (orderId) => {
    setSelectedOrderIdForItems(orderId);
    setOrderItemsDialogOpen(true);
  };
  
  // NEW: دالة إغلاق Dialog عرض الأصناف
  const handleCloseOrderItems = () => {
    setOrderItemsDialogOpen(false);
    setSelectedOrderIdForItems(null);
  };

  return (
    <Box dir="rtl" sx={{ p: isMobile ? 1 : 2, bgcolor: mode === "dark" ? "#1F1D2B" : "#fff", pb: isMobile ? "160px" : "110px" }}>
      {/* Filters: mobile-first stacked */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: isMobile ? 1 : 2 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: "100%" }}>
          {["الكل", "مكتمل", "في التجهيز"].map((status) => (
            <Button
              key={status}
              variant={filterStatus === status ? "contained" : "outlined"}
              onClick={() => setFilterStatus(status)}
              sx={{
                minWidth: 86,
                px: isMobile ? 2.25 : 2,
                py: isMobile ? 0.9 : 0.8,
                fontWeight: 700,
                fontSize: isMobile ? 14 : 13,
                ...(filterStatus === status && { bgcolor: "#814C3B", color: "#fff" }),
              }}
            >
              {status}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexDirection: "column", width: "100%" }}>
          <TextField
            placeholder="البحث عن الطلبات..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={() => setIsCreateOpen(true)} fullWidth sx={{ py: 1 }}>
              إنشاء
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Orders list - single column */}
      <Grid container spacing={2}>
        {filteredOrders.map((order, idx) => (
          <Grid item xs={12} key={order.id}>
            <Paper
              sx={{
                bgcolor: mode === "dark" ? "#232334" : "#fff",
                color: mode === "dark" ? "#fff" : "#232334",
                borderRadius: isMobile ? 2 : 3,
                boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                p: isMobile ? 1.25 : 2,
                minHeight: isMobile ? 100 : 120,
                position: "relative",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? 0.8 : 1.2,
              }}
              onClick={() => {
                setSelectedOrderId(order.id);
                setIsAddItemsOpen(true);
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: isMobile ? 15 : 16 }}>{order.customer}</Typography>
                  <Typography sx={{ fontSize: 13, color: mode === "dark" ? "#cfcff0" : "#666" }}>{order.phone}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  {/* NEW: أيقونة عدد الأصناف */}
                  {order.itemsCount > 0 && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenOrderItems(order.id);
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <Badge
                        badgeContent={order.itemsCount}
                        color="error"
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "8px",
                            fontWeight: 700,
                            minWidth: "14px",
                            height: "14px",
                            padding: "0 3px",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: mode === "dark" ? "#1a1a2e" : "#fff",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                            border: `2px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                            transition: "all 0.2s",
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                        >
                          <RestaurantMenuIcon
                            sx={{
                              fontSize: 14,
                              color: mode === "dark" ? "#fff" : "#666",
                            }}
                          />
                        </Box>
                      </Badge>
                    </Box>
                  )}
                  <Box sx={{ bgcolor: "#1ecb6b", color: "#fff", px: isMobile ? 1.6 : 2, py: 0.45, borderRadius: 2, fontWeight: 800 }}>
                    {order.orderNumber}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{order.status}</Typography>
                    <Tooltip title="عرض الأصناف">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrderId(order.id);
                          setSelectedOrderDetails(orders.find((o) => o.id === order.id) || order);
                          setIsViewOpen(true);
                        }}
                        sx={{ color: mode === "dark" ? "#fff" : "#232334" }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography sx={{ fontSize: 14, color: mode === "dark" ? "#d0d0e6" : "#444" }}>{order.address}</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: isMobile ? 15 : 16 }}>{order.time}</Typography>
                </Box>
                <Box sx={{ textAlign: "center", minWidth: 70 }}>
                </Box>
              </Box>
              <Box sx={{ position: "absolute", bottom: 12, left: 12 }}>
                <Box sx={{ bgcolor: "#F8BBD0", color: "#000", fontWeight: 800, fontSize: isMobile ? 14 : 16, px: 2, py: 0.5, borderRadius: 2 }}>
                  ${order.total}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Floating create button */}
      <Box sx={{ position: "fixed", bottom: 18, left: 18, zIndex: 1600 }}>
        <Button
          variant="contained"
          onClick={() => setIsCreateOpen(true)}
          startIcon={<AddIcon />}
          sx={{ bgcolor: "#EA7C69", color: "#fff", borderRadius: 3, px: 2.5, py: 1 }}
        >
          إنشاء
        </Button>
      </Box>

      {/* Create modal as bottom sheet (mobile-first) */}
      {isCreateOpen && (
        <Box sx={{ position: "fixed", left: 0, bottom: 0, width: "100vw", zIndex: 1400, display: "flex", justifyContent: "center" }}>
          <Box sx={{ bgcolor: mode === "dark" ? "#232334" : "#fff", borderTopLeftRadius: 16, borderTopRightRadius: 16, boxShadow: 8, p: 2, width: "100%", maxHeight: "98vh", overflowY: "auto", position: "relative" }}>
            <CreateOrderForm onSuccess={(res) => {
              const createdId = res?.id || res?.data?.order_id;
              if (!createdId) { alert("❌ فشل في جلب رقم الطلب الجديد"); return; }
              setSelectedOrderId(createdId); setIsCreateOpen(false); setIsAddItemsOpen(true); loadOrders();
            }} />
            <IconButton onClick={() => setIsCreateOpen(false)} sx={{ position: "absolute", top: 12, left: 12, color: "#EA7C69" }}>
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* View items modal - bottom sheet full-width on phones */}
      {isViewOpen && selectedOrderDetails && (
        <Box sx={{ position: "fixed", left: 0, bottom: 0, width: "100vw", zIndex: 1400 }} onClick={() => setIsViewOpen(false)}>
          <Box sx={{ bgcolor: mode === "dark" ? "#232334" : "#fff", color: mode === "dark" ? "#fff" : "#232334", borderTopLeftRadius: 16, borderTopRightRadius: 16, boxShadow: 8, p: 2, width: "100%", maxHeight: "88vh", overflowY: "auto", position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <IconButton onClick={() => setIsViewOpen(false)} sx={{ position: "absolute", left: 12, top: 12, color: "#EA7C69" }}>
              <ClearIcon />
            </IconButton>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>أصناف الطلب - {selectedOrderDetails.orderNumber || `#${selectedOrderDetails.id}`}</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>العميل: {selectedOrderDetails.customer} — الحالة: {selectedOrderDetails.status}</Typography>
            {/* simplified responsive items list */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 80px", sm: "1fr 80px 100px 100px" }, gap: 1, mb: 1, fontWeight: 800 }}>
              <Typography>الاسم</Typography>
              <Typography sx={{ display: { xs: "block", sm: "block" } }}>الكمية</Typography>
              {/** other columns hidden on very small screens */}
              <Box sx={{ display: { xs: "none", sm: "contents" } }}>
                <Typography>السعر</Typography>
                <Typography>المجموع</Typography>
              </Box>
            </Box>
            {(selectedOrderDetails.items || []).length === 0 ? <Typography sx={{ color: "#b0bec5" }}>لا توجد أصناف في هذا الطلب.</Typography> : (selectedOrderDetails.items || []).map((it) => (
              <Box key={it.id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 80px", sm: "1fr 80px 100px 100px" }, gap: 1, py: 0.5, borderBottom: "1px dashed rgba(0,0,0,0.06)" }}>
                <Typography sx={{ fontWeight: 700 }}>{it.product_name}</Typography>
                <Typography>{it.quantity}</Typography>
                <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
                  <Typography>{it.price}</Typography>
                  <Typography>{it.total}</Typography>
                </Box>
                {isMobile && <Box sx={{ textAlign: "right" }}><Typography variant="caption">سعر: {it.price}</Typography></Box>}
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" onClick={() => { setIsViewOpen(false); loadOrders(); }}>إغلاق وتحديث</Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Existing TakeawayModalContent usage */}
      <TakeawayModalContent
        open={isAddItemsOpen}
        onClose={() => setIsAddItemsOpen(false)}
        type="addItem"
        orderId={selectedOrderId}
        tableId={null}
        rootId={localStorage.getItem("branch_id")}
        onSuccess={() => {
          setIsAddItemsOpen(false);
          loadOrders();
        }}
      />

      {/* NEW: Dialog عرض أصناف الأوردر */}
      <OrderItemsDialog
        open={orderItemsDialogOpen}
        onClose={handleCloseOrderItems}
        orderId={selectedOrderIdForItems}
      />
    </Box>
  );
};

export default TakeawaySection;
